import { useMemo } from 'react'
import { recordGateEntity } from './accessRules'
import { isPermitted, resolveAccess, resolveRecordGate } from './resolveAccess'
import { RECORD_PAGE_KEYS, ROUTE_REGISTRY, ROUTE_REGISTRY_ENTRIES } from './routeRegistry'
import { useAccessSources } from './useAccessSources'
import { useIsPermittedInList } from './useIsPermittedInList'
import type { AccessAnswer, AccessSources, RecordAccessRule } from './accessRules'
import type { AskedRecord } from './askedRecord'
import type { RecordPageEntity, RecordRouteKey, RouteDefinition } from './routeRegistry'
import type { RouteKey } from '@omnea-app/fe-routing/routes/routeKeys'
import type { PermissionSetEntity } from '@omnea-digital/authz-model'

const answerFor = (definition: RouteDefinition, sources: AccessSources, entity: PermissionSetEntity | null): AccessAnswer => {
  const answer = resolveAccess(definition, sources)
  const record = definition.record
  // A record rule of another entity has no answer here, and `resolveAccess` throws rather than
  // guess one, so those routes keep the answer their address alone gives.
  const readsRecord = record != null && recordGateEntity(record) === entity
  return answer === 'allowed' && readsRecord ? resolveRecordGate(record, sources) : answer
}

const answersFor = (sources: AccessSources, entity: PermissionSetEntity | null): ReadonlyMap<RouteKey, AccessAnswer> => {
  const answers = new Map<RouteKey, AccessAnswer>()

  for (const [key, definition] of ROUTE_REGISTRY_ENTRIES) {
    answers.set(key, answerFor(definition, sources, entity))
  }

  return answers
}

const isRecordPageEntity = (routeKeyOrEntity: RouteKey | RecordPageEntity): routeKeyOrEntity is RecordPageEntity =>
  Object.hasOwn(RECORD_PAGE_KEYS, routeKeyOrEntity)

/**
 * What every route answers for the current caller, so enforcement and the links that point at a route
 * cannot disagree.
 */
export const useRouteAccess = (): ReadonlyMap<RouteKey, AccessAnswer> => {
  const sources = useAccessSources()

  return useMemo(() => answersFor(sources, null), [sources])
}

/**
 * The same map for navigation sitting on one record, with the record half of that entity's detail
 * routes folded in — so a tab whose route carries a verb narrower than `canView` hides rather than
 * leading to a not-found.
 *
 * Separate from {@link useRouteAccess} because `RouteAccessGate` reads that map to decide the route
 * half alone: a denied route redirects, where a denied record must not say the record exists.
 */
export const useRecordRouteAccess = (asked: AskedRecord<PermissionSetEntity>): ReadonlyMap<RouteKey, AccessAnswer> => {
  const sources = useAccessSources(asked)
  const { entity } = asked

  return useMemo(() => answersFor(sources, entity), [sources, entity])
}

/**
 * Whether the caller may reach one route, reading a withheld answer as no; an entity names that record's
 * own page. A record route with no id yet answers no. A surface that must say something distinct while
 * the answer is pending reads the route-access map instead.
 */
export function useCanAccessRoute(routeKey: RouteKey): boolean
export function useCanAccessRoute(routeKey: RecordRouteKey, recordId: string | undefined): boolean
export function useCanAccessRoute(entity: RecordPageEntity, recordId: string | undefined): boolean
export function useCanAccessRoute(routeKeyOrEntity: RouteKey | RecordPageEntity, recordId?: string): boolean {
  const definition: RouteDefinition =
    ROUTE_REGISTRY[isRecordPageEntity(routeKeyOrEntity) ? RECORD_PAGE_KEYS[routeKeyOrEntity] : routeKeyOrEntity]
  const recordRule = definition.record
  const routeAllowed = isPermitted(definition, useAccessSources())
  const askedRecord =
    routeAllowed && recordRule != null && recordId != null ? { entity: recordGateEntity(recordRule), recordId } : undefined
  const sources = useAccessSources(askedRecord)

  if (recordRule == null) return routeAllowed
  if (askedRecord == null) return false

  return resolveRecordGate(recordRule, sources) === 'allowed'
}

// The batched list check asks one permission per record, so it stands in for a page gate only while that
// gate is one rule; a page whose gate grows drops out of this type and its row links stop compiling.
type ListLinkablePage = {
  [Entity in RecordPageEntity]: (typeof ROUTE_REGISTRY)[(typeof RECORD_PAGE_KEYS)[Entity]] extends { record: RecordAccessRule }
    ? Entity
    : never
}[RecordPageEntity]

/**
 * `useCanAccessRoute(entity, id)` for a link on a list row: rows that mount together share one batched
 * check of the record, as {@link useIsPermittedInList} does.
 */
export const useCanAccessRecordPageInList = (entity: ListLinkablePage, recordId: string | undefined): boolean => {
  const definition = ROUTE_REGISTRY[RECORD_PAGE_KEYS[entity]]
  const routeAllowed = isPermitted(definition, useAccessSources())
  const recordAllowed = useIsPermittedInList(definition.record, routeAllowed ? recordId : undefined)

  return routeAllowed && recordAllowed
}
